/**
 * Lokasyon notları modalı — 25.08 md, madde 1.
 *
 * `notlar` tablosu PHP'den beri kullanılıyor (2300+ kayıt, hâlâ yazılıyor) ama
 * React tarafında ne gösterimi ne eklemesi vardı. Bu modal iki listeyi birlikte
 * gösterir:
 *   - Lokasyonun kendi notları
 *   - Bağlı olduğu projenin SAF proje notları (lokasyon alanı boş olanlar)
 * Ayrım önemli: bazı eski kayıtlar hem lokasyon hem proje taşıyor; "aynı proje"
 * kuralı tek başına uygulansaydı başka lokasyonların notları buraya sızardı.
 *
 * Yönetim işaretli notlar yalnızca yetkiliye ve notu ekleyene görünür (backend
 * süzer); işaret kutusu da yalnız yetkiliye açılır.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Modal, Button, Input, Select, DatePicker, Checkbox, List, Tag, Empty,
  Space, Spin, Popconfirm, Tabs, message,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { API_BASE_URL } from '../../../utils/config';

const { TextArea } = Input;

const TIP_RENK = { 0: 'default', 1: 'blue', 2: 'red', 3: 'purple' };

const NotListesi = ({ veriler, bosMetin, onSil, silinebilirMi }) => {
  if (!veriler.length) return <Empty description={bosMetin} image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  return (
    <List
      size="small"
      dataSource={veriler}
      renderItem={(n) => (
        <List.Item
          actions={
            silinebilirMi(n)
              ? [
                  <Popconfirm
                    key="sil"
                    title="Not kaldırılsın mı?"
                    okText="Kaldır"
                    cancelText="Vazgeç"
                    onConfirm={() => onSil(n.id)}
                  >
                    <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                  </Popconfirm>,
                ]
              : []
          }
        >
          <List.Item.Meta
            title={
              <Space size={6} wrap>
                <strong>{n.baslik}</strong>
                <Tag color={TIP_RENK[n.tip] || 'default'}>{n.tip_adi}</Tag>
                {n.yonetim === 1 && <Tag color="gold">Yönetim</Tag>}
                {n.proje_adi && <Tag>{n.proje_adi}</Tag>}
              </Space>
            }
            description={
              <div>
                {n.aciklama && (
                  <div style={{ whiteSpace: 'pre-wrap', marginBottom: 4 }}>{n.aciklama}</div>
                )}
                <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                  {n.tarih} · {n.ekleyen_adi || 'Bilinmiyor'}
                </span>
              </div>
            }
          />
        </List.Item>
      )}
    />
  );
};

const NotlarModal = ({ open, onClose, maptriksId, kullaniciNo }) => {
  const [yukleniyor, setYukleniyor] = useState(false);
  const [veri, setVeri] = useState({ lokasyon_notlari: [], proje_notlari: [], tipler: {} });
  const [ekleAcik, setEkleAcik] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [form, setForm] = useState({ baslik: '', aciklama: '', tip: 0, yonetim: false, tarih: dayjs() });

  const getir = useCallback(async () => {
    if (!maptriksId) return;
    setYukleniyor(true);
    try {
      const r = await axios.get(`${API_BASE_URL}/lokasyon-notlari/${maptriksId}`);
      setVeri(r.data || { lokasyon_notlari: [], proje_notlari: [] });
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Notlar alınamadı');
    } finally {
      setYukleniyor(false);
    }
  }, [maptriksId]);

  useEffect(() => { if (open) getir(); }, [open, getir]);

  const ekle = useCallback(async () => {
    if (!form.baslik.trim()) { message.warning('Başlık girin'); return; }
    setKaydediliyor(true);
    try {
      await axios.post(`${API_BASE_URL}/lokasyon-notlari/${maptriksId}`, {
        baslik: form.baslik.trim(),
        aciklama: form.aciklama.trim(),
        tip: form.tip,
        yonetim: form.yonetim ? 1 : 0,
        tarih: (form.tarih || dayjs()).format('YYYY-MM-DD'),
      });
      message.success('Not eklendi');
      setForm({ baslik: '', aciklama: '', tip: 0, yonetim: false, tarih: dayjs() });
      setEkleAcik(false);
      getir();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Not eklenemedi');
    } finally {
      setKaydediliyor(false);
    }
  }, [form, maptriksId, getir]);

  const sil = useCallback(async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/lokasyon-notlari/not/${id}`);
      message.success('Not kaldırıldı');
      getir();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Not kaldırılamadı');
    }
  }, [getir]);

  // Backend de aynı kuralı uyguluyor; buradaki kontrol yalnızca butonu gizlemek için.
  const silinebilirMi = (n) => veri.yonetim_yetkisi || n.ekleyen === kullaniciNo;

  const tipSecenekleri = Object.entries(veri.tipler || { 0: 'Genel' })
    .map(([k, v]) => ({ value: Number(k), label: v }));

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={760}
      title={`Notlar — Lokasyon ${maptriksId}`}
      footer={<Button onClick={onClose}>Kapat</Button>}
      destroyOnClose
    >
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setEkleAcik((v) => !v)}>
          {ekleAcik ? 'Formu Kapat' : 'Not Ekle'}
        </Button>
      </Space>

      {ekleAcik && (
        <div style={{ border: '1px solid #d9d9d9', borderRadius: 8, padding: 12, marginBottom: 14 }}>
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            <Input
              placeholder="Başlık"
              maxLength={250}
              value={form.baslik}
              onChange={(e) => setForm({ ...form, baslik: e.target.value })}
            />
            <TextArea
              rows={3}
              placeholder="Açıklama"
              value={form.aciklama}
              onChange={(e) => setForm({ ...form, aciklama: e.target.value })}
            />
            <Space wrap>
              <Select
                style={{ width: 150 }}
                value={form.tip}
                onChange={(v) => setForm({ ...form, tip: v })}
                options={tipSecenekleri}
              />
              <DatePicker
                value={form.tarih}
                onChange={(d) => setForm({ ...form, tarih: d })}
                format="DD.MM.YYYY"
                allowClear={false}
              />
              {veri.yonetim_yetkisi && (
                <Checkbox
                  checked={form.yonetim}
                  onChange={(e) => setForm({ ...form, yonetim: e.target.checked })}
                >
                  Yönetim notu
                </Checkbox>
              )}
              <Button type="primary" loading={kaydediliyor} onClick={ekle}>Kaydet</Button>
            </Space>
          </Space>
        </div>
      )}

      <Spin spinning={yukleniyor}>
        <Tabs
          items={[
            {
              key: 'lokasyon',
              label: `Lokasyon Notları (${veri.lokasyon_notlari?.length || 0})`,
              children: (
                <NotListesi
                  veriler={veri.lokasyon_notlari || []}
                  bosMetin="Bu lokasyona ait not yok"
                  onSil={sil}
                  silinebilirMi={silinebilirMi}
                />
              ),
            },
            {
              key: 'proje',
              label: `Proje Notları (${veri.proje_notlari?.length || 0})`,
              children: (
                <NotListesi
                  veriler={veri.proje_notlari || []}
                  bosMetin="Bağlı projenin notu yok"
                  onSil={sil}
                  silinebilirMi={silinebilirMi}
                />
              ),
            },
          ]}
        />
      </Spin>
    </Modal>
  );
};

export default NotlarModal;
